import { useState, useRef } from 'react';
// import { useDeepCompareEffect, useDebounceEffect } from 'ahooks';
import type { UseKPITrendsProps } from '@intelligence/pages/TopCharts/PCGames/_config/type';
import { fetchPCConsoleKPITrendsCompareTrends } from '@intelligence/services/topcharts';
import { KPITrendsPageConfig } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_config';
import { getHasDataValues } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/Stats/_utils';
import { getDeviceValuesByPageId } from '@/pages/intelligence/pages/TopCharts/PCGames/_config/utils';
import {
  PC_CONSOLE_COMBINE_ENTITY_TYPE,
  TopchartPageId,
} from '@intelligence/pages/TopCharts/_config/topChartsConst';
import {
  allMetricsOptions,
  defaultMetricsSaveParams,
} from '../components/MetricsSelection/config';
import {
  formatParams,
  formatPlatformPieChartParams,
  formatPlatformPieData,
  formatTrendsData,
  formatByCountryData,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/hooks/utils';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import {
  getSourceMetricsByValue,
  getMetricOptionByValue,
  DEFAULTKPITRENDSSHOWRTLIST,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';
import { isEmpty } from '@/pages/intelligence/utils/commonUtils';
import { getDefaultTime } from '@/pages/intelligence/utils/topchartsUtils';
import {
  isKpiTrendsHourlyGranularity,
  syncFilterRangeFromResponse,
  resolveHourlyBackfillRange,
  getHourlyTableMetricOptions,
  filterVisMetricsOptionsForGranularity,
  findFirstNonHourlyCcuMetric,
  KPI_TRENDS_HOURLY_CCU_METRIC,
} from '../hourSegmentUtils';

// useKPITrends获取数据与接口交互的hooks
const useKPITrendsNew = (props: UseKPITrendsProps) => {
  const {
    gameId,
    entityType = PC_CONSOLE_COMBINE_ENTITY_TYPE,
    setFilterValue,
    filterValue,
    getEarliestDate,
    jumpVisualMetrics,
    regionOptions,
    // defaultVisualMetricsValue,
  } = props || {};

  const [fetchParams, setFetchParams] = useState({
    unified_ids: [gameId],
    entity_type: entityType,
    region_type: 'market',
    regions: ['global'],
    date_type: 'monthly',
    start_date: '',
    end_date: '',
    device: getDeviceValuesByPageId(TopchartPageId.PCConsoleMetricsTable),
    source_metrics: getSourceMetricsByValue(DEFAULTKPITRENDSSHOWRTLIST),
    data_method: 'date', // 固定
    segment_list: ['All'],
  } as any);

  const [devicePieDatas, setDevicePieDatas] = useState<any>({});
  const [deviceTrendsDatas, setDeviceTrendsDatas] = useState<any>([]);
  // By Country 折线图数据（按国家分组，Top5 + Others）
  const [countryTrendsData, setCountryTrendsData] = useState<any>([]);
  // 表格数据
  const [trendsTableDataList, setTrendsTableDataList] = useState<any>([]);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [fetchPlatFormDataLoading, setFetchPlatFormDataLoading] =
    useState(false);
  const [pieData, setPieData] = useState<any[]>([]);
  const [sumAvgMaxList, setSumAvgMaxList] = useState([]);
  // Calibration flags from API response
  const [calibrationFlags, setCalibrationFlags] = useState<any>(null);
  // 用于保存和对比MetricSelection的筛选器值
  const [metricSelectionSaveValue, setMetricSelectionSaveValue] = useState(
    defaultMetricsSaveParams,
  );
  const { setFilterExtend, filterExtend = {} } = useFilterExtend({});
  // Metrics 选项
  const [selectVisMetricsOptions, setSelectVisMetricsOptions] = useState([]);
  // getMetricOptionByValue(DEFAULTKPITRENDSSHOWRTLIST),
  // 选中 Metrics 值
  const [visualMetricValue, setVisualMetricValue] = useState<string>();
  const [metricsLoading, setMetricsLoading] = useState(true);
  const isCancelledRef = useRef(false);

  const abortControllerRef = useRef<AbortController>();
  const fetchRequestIdRef = useRef(0);

  // 获取KPITrends数据
  const getIndexPageData = async (
    params: any,
    filterValueData: any = filterValue,
  ) => {
    const requestId = ++fetchRequestIdRef.current;
    setFetchLoading(true);
    let trendRes = null;
    let nextFilterValue: any = null;
    const newFetchParams = {
      ...fetchParams,
      ...params,
      region_type: 'market',
    };
    setFetchParams(newFetchParams);
    const trendMetricSelection =
      filterValueData?.metric_selection_for_trends?.value || {};
    // 可视化指标下拉与表格列均按 allMetricsOptions 配置顺序展示
    const metricOptionsRaw =
        trendMetricSelection?.metricsOptionsByConfigOrder?.length
          ? trendMetricSelection.metricsOptionsByConfigOrder
          : trendMetricSelection?.metricsOptions ||
      filterValueData?.metric_selection_for_trends?.value
        ?.metricsOptionsByConfigOrder ||
      [];
    let metricOptions =
      isKpiTrendsHourlyGranularity(newFetchParams.date_type) &&
      !metricOptionsRaw.length
        ? getHourlyTableMetricOptions()
        : metricOptionsRaw;
    if (
      !isKpiTrendsHourlyGranularity(newFetchParams.date_type) &&
      !metricOptions.length
    ) {
      metricOptions = getMetricOptionByValue(DEFAULTKPITRENDSSHOWRTLIST, true);
    }

    if (
      isEmpty(newFetchParams?.unified_ids) ||
      isEmpty(newFetchParams?.source_metrics)
    ) {
      if (requestId === fetchRequestIdRef.current) {
        setFetchLoading(false);
      }
      return { trendRes: null, nextFilterValue: null };
    }

    try {
      // 当前请求发起时将之前请求取消
      abortControllerRef.current?.abort();
      abortControllerRef.current = new AbortController();
      trendRes = await fetchPCConsoleKPITrendsCompareTrends(
        // 适配 compare 接口参数
        formatParams(newFetchParams),
        {
          signal: abortControllerRef.current?.signal,
        },
      );
      const { req = {}, data = {} } = trendRes || {};

      if (requestId !== fetchRequestIdRef.current) {
        return { trendRes, nextFilterValue: null };
      }
      
      // Extract calibration flags from data[0] (first game's data)
      const firstGameData = data?.[0] || {};
      const is_mscience_daily = firstGameData.is_mscience_daily;
      const is_vgi_daily = firstGameData.is_vgi_daily;
      
      // Set calibration flags directly (will be parsed in component)
      setCalibrationFlags({
        is_mscience_daily,
        is_vgi_daily,
      });
      
      const { start_date: startDateTemp = '', end_date: endDateTemp = '' } =
        req;
      let startDate = '';
      let endDate = '';
      const isHourly = isKpiTrendsHourlyGranularity(newFetchParams.date_type);
      if (isHourly) {
        const resolved = resolveHourlyBackfillRange(
          startDateTemp,
          endDateTemp,
          data,
        );
        startDate = resolved.startDate;
        endDate = resolved.endDate;
      } else if (startDateTemp && endDateTemp) {
        startDate = startDateTemp;
        endDate = endDateTemp;
      } else {
        const [defautStart, defautEnd] = getDefaultTime(
          newFetchParams.date_type,
        );
        startDate = defautStart;
        endDate = defautEnd;
      }
      // 调整 compare 接口数据格式为当前需要的格式
      let trends: any[] = [];
      let sum_avg: any = [];

      if (newFetchParams?.b_group_by_market) {
        // By Country 模式：生成表格数据（按日期聚合各国家 value）+ 折线图数据（按国家分组 Top5 + Others）
        const byCountryResult = formatByCountryData({
          data,
          dateType: newFetchParams.date_type,
          responseQueryDate: {
            startDate,
            endDate,
          },
          regionsCountries: regionOptions,
        });
        trends = byCountryResult.trends;
        sum_avg = byCountryResult.sum_avg;
        setCountryTrendsData(byCountryResult.countryTrendsData);
      } else {
        // 普通模式
        const normalResult = formatTrendsData({
          data,
          dateType: newFetchParams.date_type,
          responseQueryDate: {
            startDate,
            endDate,
          },
        });
        trends = normalResult.trends;
        sum_avg = normalResult.sum_avg;
        setCountryTrendsData([]);
      }

      setTrendsTableDataList(trends);
      setSumAvgMaxList(sum_avg);

      const newFetchParamsCur = {
        ...newFetchParams,
        start_date: startDate,
        end_date: endDate,
      };
      setFetchParams(newFetchParamsCur);
      const syncedFilterValue = syncFilterRangeFromResponse({
        filterValueData,
        dateType: newFetchParams.date_type,
        startDate,
        endDate,
      });
      nextFilterValue = syncedFilterValue;
      setFilterValue(syncedFilterValue);
      setFilterExtend(syncedFilterValue);

      const hasDataMetricsOption = getHasDataValues(
        metricOptions,
        trends,
        KPITrendsPageConfig?.getMetricValueOfCvm,
      );

      // 不是指标跳转需要特殊勾选指定指标的情况下，默认勾选有数据的指标
      if (jumpVisualMetrics?.current === null) {
        const isHourlyRequest = isKpiTrendsHourlyGranularity(
          newFetchParams.date_type,
        );
        const isStaleHourlyVisualMetric =
          !isHourlyRequest &&
          visualMetricValue === KPI_TRENDS_HOURLY_CCU_METRIC;
        const isCurrentMetricValid = Boolean(
          visualMetricValue &&
            !isStaleHourlyVisualMetric &&
            hasDataMetricsOption?.some(
              (metric) => metric.value === visualMetricValue,
            ),
        );

        const resolvedVisualMetric =
          (isCurrentMetricValid ? visualMetricValue : undefined) ||
          hasDataMetricsOption?.[0]?.value ||
          findFirstNonHourlyCcuMetric(metricOptions);

        if (resolvedVisualMetric) {
          setVisualMetricValue(resolvedVisualMetric);
        }
      } else {
        // 指标跳转，需要勾选指定指标的情况下，需要清楚暂存的指定勾选的指标
        if (jumpVisualMetrics?.current) {
          jumpVisualMetrics.current = null;
        }
      }

      setSelectVisMetricsOptions(
        filterVisMetricsOptionsForGranularity(
          hasDataMetricsOption || [],
          newFetchParams.date_type,
        ),
      );

      // 获取最早有数据时间
      getEarliestDate?.(newFetchParamsCur);
      isCancelledRef.current = false;
    } catch (err) {
      if (requestId !== fetchRequestIdRef.current) {
        return { trendRes, nextFilterValue: null };
      }
      isCancelledRef.current = err?.code === 999;
      if (err?.code === 999) {
        return { trendRes, nextFilterValue: null };
      }
      setTrendsTableDataList([]);
      setSumAvgMaxList([]);
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setFetchLoading(false);
        setMetricsLoading(false);
      }
    }
    return { trendRes, nextFilterValue };
  };

  const getSourceObj = (metric?: string) => {
    if (!metric) return;
    const metricObj: any =
      allMetricsOptions?.find((item) => item.value === metric) || {};
    return metricObj || ({} as any);
  };

  // 获取KPITrends-饼图及by Platform数据
  const getKPITrendsPiePlatformData = async ({
    params,
    currentMetricValue,
    isByPlatform,
    isPie,
    isByCountry,
  }: {
    params: any;
    currentMetricValue?: string;
    isByPlatform?: boolean;
    isPie?: boolean;
    isByCountry?: boolean;
  }) => {
    // setFetchLoading(true);
    setFetchPlatFormDataLoading(true);
    // metric 使用调用请求方法传参 或 metric 下拉框选中值状态
    const metric = currentMetricValue || visualMetricValue;
    const newFetchParams = {
      ...fetchParams,
      ...params,
      source: getSourceObj(metric)?.dataSource,
      unified_id: gameId,
      b_true_data: true,
    } as any;

    try {
      const trendRes = await fetchPCConsoleKPITrendsCompareTrends(
        // 适配 compare 接口参数
        formatPlatformPieChartParams({
          fetchParams: newFetchParams,
          isByPlatform,
          isPie,
          isByCountry,
        }),
      );
      // console.log('trendRestrendRes', trendRes);
      const { data = {} as any, req = {} } = trendRes || {};
      const { start_date: startDate = '', end_date: endDate = '' } = req;

      // 调整 compare 接口数据格式为当前需要的格式
      const {
        pieDataByPlatform = {},
        trendsDataByPlatform = [],
        pieData = {},
      } = formatPlatformPieData({
        data,
        isPie,
        isByPlatform,
        dateType: newFetchParams.date_type,
        responseQueryDate: { startDate, endDate },
      });

      setDevicePieDatas(pieDataByPlatform);
      setDeviceTrendsDatas(trendsDataByPlatform);
      setPieData(pieData);
    } catch {
      setDevicePieDatas({});
      setDeviceTrendsDatas([]);
      setPieData([]);
    } finally {
      setFetchPlatFormDataLoading(false);
    }
  };

  // useDebounceEffect(() => {
  //   const find = selectVisMetricsOptions?.find(item => item?.value === defaultVisualMetricsValue);
  //   if (defaultVisualMetricsValue && find) {
  //     setVisualMetricValue(defaultVisualMetricsValue);
  //   }
  // }, [defaultVisualMetricsValue, selectVisMetricsOptions?.length]);

  return {
    fetchParams,
    devicePieDatas,
    deviceTrendsDatas,
    countryTrendsData,
    trendsTableDataList,
    fetchLoading,
    fetchPlatFormDataLoading,
    setFetchLoading,
    getIndexPageData,
    pieData,
    visualMetricValue,
    setVisualMetricValue,
    selectVisMetricsOptions,
    sumAvgMaxList,
    metricsLoading,
    getKPITrendsPiePlatformData,
    metricSelectionSaveValue,
    setMetricSelectionSaveValue,
    isCancelledRef,
    calibrationFlags,
  };
};

export default useKPITrendsNew;
